/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { ToastType } from '@components/Toast/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { FalveyInsuranceQuoteInLoad } from '@store/ltl/types';
import {
  fetchCopyOrder,
  fetchOrder,
  fetchOrderLoadboard,
  postCopiedOrder,
  saveOrder,
  voidOrder,
} from '@store/orders/services/orderServices';
import { sendEDIUpdate } from '@store/orders/services/commentsDocumentsTrackingServices';
import { voidVendor } from '@store/orders/services/vendorServices';
import {
  orderStore,
  resetCarrierStore,
  setCopiedOrderId,
  setLoadSureStore,
  setLoadTenderStore,
  setOrderLoading,
  setOrderStore,
} from '@store/orders/store';
import {
  EdiUpdate,
  LoadEdiStatusUpdatePayload,
  TabType,
} from '@store/orders/types';
import { userStore } from '@store/user/store';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { isDateLessThanCurrentDate } from '@utils/dateUtils';
import { logError } from '@utils/errorLogging';
import { routeOrderDetails } from '@utils/routeOrderDetails';
import { handleToast } from '@utils/utils';
import { DateTime } from 'luxon';
import { produce, unwrap } from 'solid-js/store';
import { v4 as uuidv4 } from 'uuid';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import {
  clearOrderChangeBaseline,
  prepareOrderChangeSummary,
  setOrderChangeBaseline,
} from '@changeTracking/pages/orderDetails/formChangeTracking';

import { copyLoad, updateCustomerAndVendorPay } from '../helper';
import { updateOrderState } from './orderFormActions';
import { getCarrierDetail, getCarrierFeatures } from './carrierActions';
import { fetchPostLoadByPostingId } from './postLoadActions';
import { loadCustomerInfo } from './orderCustomerActions';
import { mergeOrderWithQuote } from './ltlQuoteActions';

type NavigateFunction = (to: string) => void;

export const TrySaveOrder = async (
  navigate?: NavigateFunction,
  params?: string,
): Promise<boolean> => {
  let saved = false;
  await saveOrderDetails((order: IOrderViewModel) => {
    saved = Boolean(order.id);
    order.loads
      .filter((load: ILoadEditViewModel) => load.mode === 'LTL')
      .forEach((load) => {
        load.newLtlQuoteId = orderStore.quoteId as unknown as number;
      });
    if (orderStore.isCreate) {
      const isNative = routeOrderDetails(order.id);
      if (isNative) {
        navigate && navigate(`/order/details/${order.id}`);
      }
    } else {
      if (params !== undefined && params !== '') {
        navigate && navigate(`/order/details/${order.id}`);
      }
    }

    const updatedLoads = order.loads.map((load) => {
      return {
        ...load,
        stops:
          load.stops?.map((s) => {
            return {
              ...s,
              shouldSendEdiBooleanVals: {
                shouldSendPickupAppointmentAuto214: false,
                shouldSendDropoffAppointmentAuto214: false,
                shouldSendDropoffDriverInAuto214: false,
                shouldSendPickupDriverInAuto214: false,
                shouldSendPickupDriverOutAuto214: false,
                shouldSendDropoffDriverOutAuto214: false,
              },
            };
          }) ?? [],
        quoteItems: load.items,
        quoteStops: load.stops,
      };
    });

    //Putting this in a settimout so as to not trigger the createEffect for mergeOrderWithQuote
    setTimeout(() => {
      order.loads = updatedLoads as ILoadEditViewModel[];
      setOrderStore('order', order);
      setOrderStore('isCreate', false);
      setOrderStore('matched', false);
      setLoadSureStore('isDirty', []);
    }, 0);
  });
  return saved;
};

export const fetchOrderForLoadBoard = async (
  orderId: string,
  loadNum: string,
) => {
  try {
    const order = await fetchOrderLoadboard(orderId);
    const index = order.loads.findIndex((l) => l.loadNum === loadNum);

    setOrderStore(() => ({
      order: order,
      activeTab: {
        type: TabType.load,
        index: index,
      },
      isCreate: false,
    }));
    setOrderStore(
      'order',
      'loads',
      index,
      'stops',
      () => true,
      'shouldSendEdiBooleanVals',
      {
        shouldSendPickupAppointmentAuto214: false,
        shouldSendDropoffAppointmentAuto214: false,
        shouldSendDropoffDriverInAuto214: false,
        shouldSendPickupDriverInAuto214: false,
        shouldSendPickupDriverOutAuto214: false,
        shouldSendDropoffDriverOutAuto214: false,
      },
    );
  } catch (error) {
    setOrderStore(() => ({
      isError: true,
    }));
  } finally {
    setOrderStore(() => ({
      loading: false,
    }));
  }
};

export const fetchOrderDetails = async (orderId: string, quoteId?: string) => {
  clearOrderChangeBaseline();
  try {
    setOrderLoading(true);
    const order = await fetchOrder(orderId);

    void (async () => {
      if (order.loads[0].carrierId != null) {
        await getCarrierFeatures(order.loads[0].carrierId, 0);
        await getCarrierDetail(order.loads[0].carrierId, 0);
      } else {
        resetCarrierStore();
      }

      // need to discuss
      if (order.loads[0].postingId !== null) {
        await fetchPostLoadByPostingId(order.loads[0].postingId);
      }
    })();

    const updatedLoads = order.loads.map((load) => ({
      ...load,
      quoteItems: load.items,
      quoteStops: load.stops,
      //Here be dragons
      //WARNING: NOT TO Touch below class map function else it will break the stops line items
      items: load.items?.map((item) => {
        return {
          ...item,
          class: item.class,
        };
      }),
      stops:
        load.stops?.map((s) => {
          return {
            ...s,
            shouldSendEdiBooleanVals: {
              shouldSendPickupAppointmentAuto214: false,
              shouldSendDropoffAppointmentAuto214: false,
              shouldSendDropoffDriverInAuto214: false,
              shouldSendPickupDriverInAuto214: false,
              shouldSendPickupDriverOutAuto214: false,
              shouldSendDropoffDriverOutAuto214: false,
            },
          };
        }) ?? [],
    }));
    setOrderStore(() => ({
      order: {
        ...order,
        loads: updatedLoads,
      },
    }));
    if (order.customerId !== null) {
      const customerInfo = await loadCustomerInfo(order.customerId);
      if (
        customerInfo?.operationsRepId &&
        isFeatureFlagEnabled(FeatureFlags.OperationsRep)() &&
        !order.operationsId
      ) {
        updateOrderState({
          operationsId: customerInfo.operationsRepId,
        });
      }
    }
    //Check Falvey Load Index available or not
    const falveyLoadIndex = order.loads.findIndex((load) =>
      Boolean(load.ltlQuote?.ltlInsurance),
    );

    if (
      falveyLoadIndex !== -1 &&
      order.loads[falveyLoadIndex].ltlQuote?.ltlInsurance &&
      order.loads[falveyLoadIndex].ltlQuote.ltlInsurance.shippingSumInsured > 0
    ) {
      const { shippingSumInsured, quotePremium, quoteServiceFee } =
        order.loads[falveyLoadIndex].ltlQuote.ltlInsurance;
      // NOTE: Insert customer and vendor pay if user save the order and added declared and not saved the order and refresh the page
      if (order.lineItems.findIndex((i) => i.type === 'LTL Insurance') === -1) {
        updateCustomerAndVendorPay(
          {
            shippingSumInsured,
            quotePremium,
            quoteServiceFee,
          } as FalveyInsuranceQuoteInLoad,
          'insert',
          updatedLoads[falveyLoadIndex].ltlQuote?.ltlInsurance
            ?.shippingSumInsured as number,
        );
      }
    }

    if (quoteId !== undefined) {
      await mergeOrderWithQuote(quoteId, order);
    }

    setOrderChangeBaseline(unwrap(orderStore.order));
  } catch (error) {
    setOrderStore(() => ({
      isError: true,
    }));
  } finally {
    setOrderStore(() => ({
      loading: false,
    }));
  }
};

export const fetchCopyOrderDetails = async (orderId: string) => {
  try {
    setOrderLoading(true);
    const order = await fetchCopyOrder(orderId);

    if (order.loads[0].carrierId != null) {
      await getCarrierFeatures(order.loads[0].carrierId, 0);
      await getCarrierDetail(order.loads[0].carrierId, 0);
    }
    // need to discuss
    const updatedLoads = order.loads.map((load) => ({
      ...load,
      useStatusStateMachine: true,
      quoteItems: load.items,
      stops: load.stops?.map((s) => {
        if (s.stopDateTime === null || s.stopDateTime === undefined) {
          return {
            stopDateTime: undefined,
            ...s,
          };
        }
        const stopDateTime = DateTime.fromISO(s.stopDateTime);
        if (isDateLessThanCurrentDate(stopDateTime) || s.dropOff) {
          // we need to handle stopDateTime where it should go to undefined so the user can select the date
          const newStop = {
            ...s,
            stopDateTime: undefined,
            stopDateFormatted: undefined,
          };
          return newStop;
        }
        return {
          ...s,
        };
      }),
      quoteStops: load.stops,
      //Here be dragons
      //WARNING: NOT TO Touch below class map function else it will break the stops line items
      items: load.items?.map((item) => {
        return {
          ...item,
          class: item.class,
        };
      }),
    }));
    setOrderStore(() => ({
      order: {
        ...order,
        loads: updatedLoads,
      },
    }));
  } catch (error) {
    setOrderStore(() => ({
      isError: true,
    }));
  } finally {
    setOrderStore(() => ({
      loading: false,
    }));
  }
};

export const copyOrder = async (copyMultiple?: boolean) => {
  const currentOrder = unwrap(orderStore.order);
  const isLTL = currentOrder.loads.some((load) => load.mode === 'LTL');
  const clearCarrierForTlModes = isFeatureFlagEnabled(
    FeatureFlags.CopyOrderClearCarrierRatesForTL,
  )();

  const newOrder: IOrderViewModel = {
    hideCustomerName: currentOrder.hideCustomerName,
    comments:
      copyMultiple !== undefined && copyMultiple
        ? []
        : [
            {
              comment: 'Copied from Order# ' + currentOrder.id,
              timestamp: new Date().toISOString(),
              id: 0,
              objectId: currentOrder.id,
              type: 'Order',
              operationType: 'Insert',
              name: userStore.user.name,
              userId: userStore.user.id,
              createdBy: userStore.user.id,
              thumbnail: undefined,
            },
          ],
    customerContact: currentOrder.customerContact,
    customerId: currentOrder.customerId,
    pointOfSaleInstructions: currentOrder.pointOfSaleInstructions,
    chargeCreditCardFee: currentOrder.chargeCreditCardFee,
    customerName: currentOrder.customerName,
    customer: currentOrder.customer,
    customerCredit: currentOrder.customerCredit,
    customerContactId: currentOrder.customerContactId,
    availableCredit: currentOrder.availableCredit,
    paymentTerms: currentOrder.paymentTerms,
    owner: currentOrder.owner,
    operationsId: currentOrder.operationsId,
    agingOut: false,
    availableServices: currentOrder.availableServices,
    metadata: [],
    customerPrePayments: undefined,
    synched: false,
    bidType: currentOrder.bidType,
    edi: currentOrder.edi,
    trackLoads: currentOrder.trackLoads,
    private: currentOrder.private,
    show214: currentOrder.show214,
    daysPastDue: 0,
    canVoidOrder: false,
    isDeleted: false,
    loadInformation: currentOrder.loadInformation,
    id: 0,
    accountingSysId: undefined,
    needsApprovalFlag: undefined,
    operationType: 'Insert',
    createdBy: 0,
    createdDate: new Date(),
    loadTenderId: null,
    lineItems: isLTL
      ? []
      : orderStore.order.lineItems
          .filter((lineItem) => lineItem.operationType !== 'Delete')
          .map((lineItem) => ({
            type: lineItem.type,
            description: lineItem.description,
            quantity: lineItem.quantity,
            rate: lineItem.rate,
            estimate: lineItem.estimate,
            adjustment: lineItem.adjustment,
            stopId: undefined,
            descriptionWithParanthesis: lineItem.descriptionWithParanthesis,
            createdDate: new Date(),
            referenceNumber: null,
            rateDetail: lineItem.rateDetail,
            operationType: 'Insert',
            id: 0,
            orderId: 0,
            status: 'Unsettled',
            readOnly: false,
            synched: false,
            settledDate: null,
          })),
    vendors: orderStore.order.vendors
      ?.filter(
        (vendor) =>
          vendor.operationType !== 'Delete' &&
          vendor.name !== 'Falvey Shippers Insurance' &&
          vendor.name !== 'Reliance Partners, LLC',
      )
      .map((vendor) => ({
        operationType: 'Insert' as const,
        id: 0,
        orderId: 0,
        synched: false,
        settledDate: null,
        description: vendor.description,
        invoicedDate: undefined,
        paidDate: undefined,
        name: vendor.name,
        mailingAddress1: vendor.mailingAddress1,
        mailingAddress2: vendor.mailingAddress2,
        mailingCity: vendor.mailingCity,
        mailingState: vendor.mailingState,
        mailingZip: vendor.mailingZip,
        countryCode: vendor.countryCode,
        serviceType: vendor.serviceType,
        serviceTypeText: vendor.serviceTypeText,
        currencyType: vendor.currencyType,
        contactName: vendor.contactName,
        contactPhone: vendor.contactPhone,
        status: 'Open' as const,
        payments: undefined,
        vendorPayDisplay: vendor.vendorPayDisplay,
        otherServiceType: vendor.otherServiceType,
        vendorContractXref: vendor.vendorContractXref
          ? {
              operationType: 'Insert' as const,
              id: 0,
              vendorMasterId: vendor.vendorContractXref.vendorMasterId,
              vendorAddressId: vendor.vendorContractXref.vendorAddressId,
              vendorServiceTypeId:
                vendor.vendorContractXref.vendorServiceTypeId,
              contacts: vendor.vendorContractXref.contacts?.map((contact) => ({
                id: contact.id,
                name: contact.name,
                email: contact.email,
                phone: contact.phone,
                phoneExt: contact.phoneExt,
              })),
              serviceTypes: vendor.vendorContractXref.serviceTypes?.map(
                (serviceType) => ({
                  id: serviceType.id,
                  name: serviceType.name,
                }),
              ),
            }
          : null,
        lineItems: isLTL
          ? []
          : vendor.lineItems
              ?.filter((lineItem) => lineItem.operationType !== 'Delete')
              .map((lineItem) => ({
                operationType: 'Insert' as const,
                id: 0,
                orderId: 0,
                status: 'Unsettled',
                readOnly: false,
                synched: false,
                settledDate: null,
                type: lineItem.type,
                description: lineItem.description,
                quantity: lineItem.quantity,
                rate: lineItem.rate,
                estimate: lineItem.estimate,
                adjustment: lineItem.adjustment,
                stopId: undefined,
                descriptionWithParanthesis: lineItem.descriptionWithParanthesis,
                createdDate: new Date(),
                referenceNumber: '',
                rateDetail: lineItem.rateDetail,
                vendorContractId: lineItem.vendorContractId,
              })),
      })),
    payments: [],
    adjustments: [],
    collectionActions: undefined,
    referenceNumber: '',
    flags: [],
    status: 'Open',
    editable: true,
    invoicedDate: undefined,
    paidDate: undefined,
    billingHold: false,
    assignedId: userStore.user.id,
    currencyType: currentOrder.currencyType,
    loads: orderStore.order.loads.map(
      (load) =>
        copyLoad(
          load,
          undefined,
          undefined,
          false,
          clearCarrierForTlModes,
        ) as unknown as ILoadEditViewModel,
    ),
  };

  if (copyMultiple === undefined || !copyMultiple) {
    const copyOrderId = uuidv4();
    setCopiedOrderId(copyOrderId);
    await postCopiedOrder(copyOrderId, newOrder);
  }
  return newOrder;
};

const loadHasActiveTonu = (load: ILoadEditViewModel): boolean =>
  load.mode !== 'LTL' &&
  (load.lineItems?.some(
    (li) => li.type === 'TONU' && li.operationType !== 'Delete',
  ) ??
    false);

//  On save, if a load has an active TONU charge, turn off its auto status
//  updates and clear stop driver in/out times (also strips pending auto-214 EDI).
//  Pure transformation: returns a new order rather than mutating the store, so a
//  failed save cannot destroy the user's entered times/EDI updates.
const withTonuStatusExclusion = (order: IOrderViewModel): IOrderViewModel => ({
  ...order,
  loads: order.loads.map((load) => {
    if (!loadHasActiveTonu(load)) return load;

    return {
      ...load,
      useStatusStateMachine: false,
      operationType:
        !load.operationType || load.operationType === 'None'
          ? 'Update'
          : load.operationType,
      stops: load.stops?.map((stop) => {
        const hasTimes =
          Boolean(stop.driverInTime) ||
          Boolean(stop.driverOutTime) ||
          (stop.ediUpdates?.some((edi) => edi.auto214) ?? false);
        if (!hasTimes) return stop;

        return {
          ...stop,
          driverInTime: null,
          driverOutTime: null,
          driverIn: undefined,
          driverOut: undefined,
          ediUpdates: stop.ediUpdates?.filter((edi) => !edi.auto214) ?? [],
          operationType:
            stop.operationType === 'None' ? 'Update' : stop.operationType,
        };
      }),
    };
  }),
});

const voidUnusedFalveyVendor = async () => {
  const vendors = orderStore.order.vendors;
  if (!vendors) return;

  const falveyVendorIndex = vendors.findIndex(
    (vendor) => vendor.name === 'Falvey Shippers Insurance',
  );
  const falveyLoadIndex = orderStore.order.loads.findIndex((load) =>
    Boolean(load.ltlQuote?.ltlInsurance),
  );
  const falveyVendorId =
    falveyVendorIndex === -1 ? 0 : Number(vendors[falveyVendorIndex].id) || 0;

  if (falveyLoadIndex !== -1 || falveyVendorId <= 0) return;

  try {
    await voidVendor(falveyVendorId, falveyVendorIndex, true);
  } catch (error) {
    handleToast(ToastType.Error, (error as Error).message);
  }
};

export const saveOrderDetails = async (
  cb?: (order: IOrderViewModel) => void,
) => {
  try {
    setOrderStore('isSaving', true);
    const excludedOrder = withTonuStatusExclusion(orderStore.order);
    let allEdiUpdates: EdiUpdate[] = [];
    const payload: IOrderViewModel = {
      ...excludedOrder,
      loads: excludedOrder.loads.map((l) => {
        return {
          ...l,
          stops:
            l.stops?.map((s, idx) => {
              const { ediUpdates } = s;

              if (s.operationType !== 'Delete') {
                allEdiUpdates = allEdiUpdates.concat(ediUpdates ?? []);
              }

              return {
                ...s,
                operationType: ['Insert', 'Delete'].includes(s.operationType)
                  ? s.operationType
                  : 'Update',
                stopOrder: idx,
              };
            }) ?? [],
        };
      }),
    };

    const changedForms =
      payload.id > 0 && !orderStore.isReadOnly
        ? prepareOrderChangeSummary(unwrap(payload)).changedForms
        : [];

    const order = await saveOrder(unwrap(payload), changedForms);
    await Promise.all(
      allEdiUpdates.map(async (update) => {
        try {
          const resp = (await sendEDIUpdate(
            update.loadId,
            update as unknown as LoadEdiStatusUpdatePayload,
          )) as unknown as { success: boolean };

          if (Boolean(resp.success)) {
            handleToast(ToastType.Success, 'EDI Update sent successfully');
          }
        } catch (error) {
          handleToast(ToastType.Error, (error as Error).message);
        }
      }),
    );
    setOrderStore('isSaving', false);
    if (
      isFeatureFlagEnabled(
        FeatureFlags.EnableLoadDetailsCarrierBounceTracking,
      )()
    ) {
      order.loads.forEach((load) => {
        load.isCarrierDirty = false;
      });
      setOrderStore(
        'order',
        'loads',
        (load, index) => {
          const savedLoad = order.loads.at(index);
          return (
            savedLoad !== undefined &&
            (load.id <= 0 || load.id === savedLoad.id) &&
            load.carrierId === savedLoad.carrierId
          );
        },
        'isCarrierDirty',
        false,
      );
    }
    cb && cb(order);
    setOrderChangeBaseline(order);
    // Delete falvey vendor if declared value is removed
    await voidUnusedFalveyVendor();
    setLoadTenderStore('loading', false);
    setOrderStore('isSaving', false);
  } catch (error) {
    logError('saveOrderDetails', error as Error);
    setLoadTenderStore('isError', true);
    setLoadTenderStore('loading', false);
    setOrderStore('isSaving', false);
    throw error;
  }
};

export const setOrderToVoid = async (orderId: number) => {
  try {
    const response = await voidOrder(
      orderId,
      CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderVoidOrder,
    );
    response?.isSuccess &&
      setOrderStore(
        produce((draft) => {
          draft.order.status = 'Void';
          draft.order.loads.forEach((load) => (load.status = 'Void'));
          if (isFeatureFlagEnabled(FeatureFlags.FeatureVoidVendors)()) {
            draft.order.vendors?.forEach((vendor) => {
              vendor.status = 'Void';
              vendor.operationType = 'None';
            });
          }
        }),
      );
    return response;
  } catch (error) {
    logError('setOrderToVoid', error as Error);
    handleToast(ToastType.Error, (error as Error).message);
    return {
      success: false,
      message: `Failed to void the order ${
        error instanceof Error ? error.message : String(error)
      }`,
    };
  }
};
